import { BorderRadius, Color } from '@persona-id/design-tokens';
import { FC } from 'react';
import styled from 'styled-components';

import {
  NODE_WIDTH,
  WorkflowCanvasCustomEvent,
} from 'dashboard/components/WorkflowEditor/WorkflowCanvas/constants';
import { BaseNodeProps } from 'dashboard/components-lib/FlowPlotter';

const PlusSign = () => (
  <svg
    role='img'
    width='8'
    height='8'
    viewBox='0 0 8 8'
    fill='none'
    xmlns='http://www.w3.org/2000/svg'
  >
    <path
      d='M4 1.66602V6.33268'
      stroke='white'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
    <path
      d='M1.66666 4H6.33332'
      stroke='white'
      strokeWidth='1.5'
      strokeLinecap='round'
      strokeLinejoin='round'
    />
  </svg>
);

const COMPONENT_HEIGHT = 24;
const CIRCLE_DIAMETER = 10;

const AddCircle = styled.div`
  position: absolute;
  display: flex;
  border-radius: 50%;
  justify-content: center;
  align-items: center;
  width: ${CIRCLE_DIAMETER}px;
  height: ${CIRCLE_DIAMETER}px;
  left: ${(NODE_WIDTH - CIRCLE_DIAMETER) / 2}px;
  top: ${(COMPONENT_HEIGHT - CIRCLE_DIAMETER) / 2}px;
  background-color: ${Color.PrimaryWhite};
  border: 2px solid ${Color.CorePurple200};
`;

const Container = styled.div`
  position: relative;
  width: ${NODE_WIDTH}px;
  height: ${COMPONENT_HEIGHT}px;
  border-radius: ${BorderRadius.Lg};
  transition: background-color 0.25s;
  cursor: pointer;

  &:hover {
    background-color: ${Color.CorePurple200};

    ${AddCircle} {
      width: ${CIRCLE_DIAMETER * 1.5}px;
      height: ${CIRCLE_DIAMETER * 1.5}px;
      left: ${(NODE_WIDTH - CIRCLE_DIAMETER * 1.5) / 2}px;
      top: ${(COMPONENT_HEIGHT - CIRCLE_DIAMETER * 1.5) / 2}px;
      background-color: ${Color.CorePurple800};
      border: 2px solid ${Color.CorePurple800};
    }

    &[aria-disabled='true'] {
      cursor: default;
      background-color: transparent;
    }
  }
`;

const BAR_WIDTH = 48;
const BAR_HEIGHT = 2;

const Bar = styled.div`
  position: absolute;
  width: ${NODE_WIDTH + BAR_WIDTH}px;
  height: ${BAR_HEIGHT}px;
  top: ${(COMPONENT_HEIGHT - BAR_HEIGHT) / 2}px;
  left: -${BAR_WIDTH / 2}px;
  border: 1px dashed ${Color.CorePurple800};
`;

export enum ClickPayloadType {
  AfterStep = 'after-step',
  FirstInBranch = 'first-in-branch',
}

interface ClickPayloadAfterStep {
  type: ClickPayloadType.AfterStep;
  previousNodeToken: string;
}

interface ClickPayloadFirstInBranch {
  type: ClickPayloadType.FirstInBranch;
  parentNodeToken: string;
  branchIndex: number;
}

interface NodePlusButtonProps {
  disabled?: boolean;
  clickPayload: ClickPayloadAfterStep | ClickPayloadFirstInBranch;
}

const NodePlusButton: FC<NodePlusButtonProps & BaseNodeProps> = ({
  disabled = false,
  clickPayload,
  isUnderDraggedItem,
  onNodeCustomEvent,
}) => {
  const addNode = () => {
    if (!disabled) {
      if (clickPayload.type === ClickPayloadType.AfterStep) {
        onNodeCustomEvent({
          key: WorkflowCanvasCustomEvent.Add,
          previousNodeToken: clickPayload.previousNodeToken,
        });
      } else {
        onNodeCustomEvent({
          key: WorkflowCanvasCustomEvent.AddToStartOfBranch,
          parentNodeToken: clickPayload.parentNodeToken,
          branchIndex: clickPayload.branchIndex,
        });
      }
    }
  };

  return (
    <Container
      aria-label={
        clickPayload.type === ClickPayloadType.AfterStep
          ? `Add step after ${clickPayload.previousNodeToken}`
          : `Add step to start of branch ${clickPayload.branchIndex + 1} under ${
              clickPayload.parentNodeToken
            }`
      }
      aria-disabled={disabled}
      role='button'
      onClick={addNode}
    >
      {!disabled && !isUnderDraggedItem && (
        <AddCircle>
          <PlusSign />
        </AddCircle>
      )}
      {isUnderDraggedItem && <Bar />}
    </Container>
  );
};

export default NodePlusButton;
